import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { getLocalizedApiMessage } from "../../../utils/apiMessageHelper";
import {
  createPaymentTypeRequest,
  getPaymentTypesData,
  removePaymentType,
  updatePaymentTypeRequest,
} from "../../../services/api/paymentTypes/paymentTypesApi";

const rejectApiResponse = (responseData, fallbackMessage) => {
  if (responseData?.success === false) {
    return responseData.message ?? fallbackMessage;
  }
  return null;
};

const PAYMENT_TYPE_CREATE_ERROR_FALLBACK = {
  en: "Failed to create payment type.",
  ar: "فشل في إنشاء نوع الدفع.",
};

const PAYMENT_TYPE_UPDATE_ERROR_FALLBACK = {
  en: "Failed to update payment type.",
  ar: "فشل في تحديث نوع الدفع.",
};

const getLang = () =>
  localStorage.getItem("language") === "ar" ? "ar" : "en";

const toastMessages = {
  delete: {
    en: "Payment type deleted successfully!",
    ar: "تم حذف نوع الدفع بنجاح!",
  },
  deleteError: {
    en: "Failed to delete payment type.",
    ar: "فشل في حذف نوع الدفع.",
  },
};

export const fetchPaymentTypesData = createAsyncThunk(
  "paymentTypes/fetchPaymentTypesData",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getPaymentTypesData();
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch payment types",
      );
    }
  },
);

export const createPaymentType = createAsyncThunk(
  "paymentTypes/createPaymentType",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await createPaymentTypeRequest(formData);

      const apiError = rejectApiResponse(
        response.data,
        PAYMENT_TYPE_CREATE_ERROR_FALLBACK,
      );
      if (apiError) {
        return rejectWithValue(apiError);
      }

      return {
        paymentType: response.data?.data,
        message: response.data?.message,
      };
    } catch (error) {
      return rejectWithValue(
        getLocalizedApiMessage(error, PAYMENT_TYPE_CREATE_ERROR_FALLBACK),
      );
    }
  },
);

export const updatePaymentType = createAsyncThunk(
  "paymentTypes/updatePaymentType",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await updatePaymentTypeRequest(id, formData);

      const apiError = rejectApiResponse(
        response.data,
        PAYMENT_TYPE_UPDATE_ERROR_FALLBACK,
      );
      if (apiError) {
        return rejectWithValue(apiError);
      }

      return {
        paymentType: response.data?.data,
        message: response.data?.message,
      };
    } catch (error) {
      return rejectWithValue(
        getLocalizedApiMessage(error, PAYMENT_TYPE_UPDATE_ERROR_FALLBACK),
      );
    }
  },
);

export const deletePaymentType = createAsyncThunk(
  "paymentTypes/deletePaymentType",
  async (id, { rejectWithValue }) => {
    try {
      await removePaymentType(id);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete payment type",
      );
    }
  },
);

const paymentTypesSlice = createSlice({
  name: "paymentTypes",
  initialState: {
    paymentTypesData: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaymentTypesData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentTypesData.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentTypesData = action.payload;
      })
      .addCase(fetchPaymentTypesData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createPaymentType.fulfilled, (state, action) => {
        if (action.payload?.paymentType) {
          state.paymentTypesData.push(action.payload.paymentType);
        }
      })
      .addCase(createPaymentType.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(updatePaymentType.fulfilled, (state, action) => {
        const paymentType = action.payload?.paymentType;
        if (!paymentType) return;

        const index = state.paymentTypesData.findIndex(
          (item) => item.id === paymentType.id,
        );
        if (index !== -1) {
          state.paymentTypesData[index] = paymentType;
        }
      })
      .addCase(updatePaymentType.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(deletePaymentType.fulfilled, (state, action) => {
        state.paymentTypesData = state.paymentTypesData.filter(
          (item) => item.id !== action.payload,
        );
        toast.success(toastMessages.delete[getLang()]);
      })
      .addCase(deletePaymentType.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.deleteError[getLang()]);
      });
  },
});

export default paymentTypesSlice.reducer;
